import React from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import CarouselVar from "components/CarouselVar/CarouselVar.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrain, faShieldAlt, faBolt, faIndustry, faRobot } from '@fortawesome/free-solid-svg-icons';
import { useRouter } from 'next/router';
import tedaiStyles from "./tedai2024/TedaiCommon.module.scss";

// Dynamic imports for components that might cause hydration issues
const Header = dynamic(() => import('components/Header/HeaderTribalism.js'), { ssr: false });
const HeaderLinks = dynamic(() => import('components/Header/HeaderLinksTribalism.js'), { ssr: false });
const Footer = dynamic(() => import('components/Footer/TribalismFooter.js'), { ssr: false });
const Section8Contacts = dynamic(() => import('pages-sections/home-page/Section8Contacts.js'), { ssr: false });
const CardExampleCardRotating = dynamic(
  () => import('components/RotatingCard/RotatingCardSmall2.js'),
  { ssr: false }
);


// Client-only wrapper component
const ClientOnly = ({ children }) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) {
    return null;
  }

  return <>{children}</>;
};


export default function TEDAI2024() {
  const classes = tedaiStyles;
  const router = useRouter();

  // Function to handle navigation
  const handleCardClick = (path) => {
    router.push(path);
  };

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
    
      <Head>
        {/* Open Graph Metadata for LinkedIn and other social platforms */}
        <meta property="og:title" content="TEDAI 2024 - Our Full Review" />
        <meta property="og:description" content="Explore our review of TEDAI 2024, covering the latest in AI with insights from leading experts." />
        <meta property="og:image" content="https://www.tribalism.com.au/img/tedai2024/home/ted-ai-header4.jpeg" />
        <meta property="og:url" content="https://www.tribalism.com.au/tedai2024" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TEDAI 2024 - Comprehensive Review" />
        <meta name="twitter:description" content="Explore our review of TEDAI 2024, covering the latest in AI with insights from leading experts." />
        <meta name="twitter:image" content="https://www.tribalism.com.au/img/tedai2024/home/ted-ai-header4.jpeg" />
        <title>TEDAI 2024 - Comprehensive Review</title>
        <meta name="description" content="Detailed coverage of TEDAI 2024, featuring insights from AI experts and thought leaders." />
      </Head>

      <ClientOnly>
      <div>
        <Header
          brand="/img/clients/TribalismLogoRev.webp"
          links={<HeaderLinks dropdownHoverColor="info" />}
          fixed
          color="transparent"
          title="TEDAI2024 - Our Full Review"
          image="/img/tedai2024/home/ted-ai-header4.jpeg"
          url="https://www.tribalism.com.au/tedai2024"
          type="article"
          description="Explore our review of TEDAI 2024, covering the latest in AI with insights from leading experts."
          changeColorOnScroll={{
            height: 400,
            color: "info",
          }}
        />

        <div className={classes.heroSection1}>
          <img
            src="/img/tedai2024/home/ted-ai-header4.jpeg"
            alt="TEDAI2024"
            className={classes.heroImage}
          />
        </div>
        
      <div className={classes.heroSection}>
        <h1 className={classes.mainTitle}>Welcome to our TEDAI2024 Review</h1>
        <h2 className={classes.subtitle}>
          Exploring the Future of AI at the Worlds Leading AI Event
        </h2>


        <p className={classes.introText}>
          <a href="/about/profile-scott-sunderland">Scott Sunderland</a> from Tribalism attended the prestigious TEDAI 2024 conference in San Francisco on October 22-23 2024,  arguably the world's leading event in AI innovation and discourse. With prominent speakers like physicist Carlo Rovelli and AI experts like Max Jaderberg, Jakob Uszkoreit, Noam Brown and Surya Ganguli, the conference spotlighted key topics: the need for more efficient AI models, the future of the workforce, AI regulation, and military concerns.
        </p>



        <p className={classes.introText}>
          Our coverage begins with a commentary of Days 1 and 2 of the conference. In further posts we will try and distill the key learnings and takeouts - focusing on tangible business implications and practical ways companies (and industry professionals)  can position themselves in this fast-evolving landscape.
        </p>
      </div>

      {/* Event Highlights Section */}
      <div className={classes.container}>
        <h2 className={classes.sectionTitle}>Event Highlights</h2>
        <p className={classes.introText}>
          Highlights from each talk and panel session from Day 1 and Day 2. Explore in-depth summaries and learn more about each topic.
        </p>

        <div className={classes.cardContainer}>
          {/* Day 1 Card with Circles Pattern */}
          <div
            className={classes.clickableCard}
            onClick={() => handleCardClick('/tedai2024/tedai2024-day1-talks-summary')}
          >
            <Card className={classes.day1Inner}>
              <div style={{ display: "flex", padding: "10px" }}>
                <div style={{
                  width: "150px",
                  flexShrink: 0,
                  marginRight: "10px"
                }}>
                  <img
                    src="/img/tedai2024/home/ted-ai-header3.jpeg"
                    alt="Day 1"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "4px"
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <CardBody>
                    <h4 className={classes.cardTitle}>Day 1: Talks</h4>
                    <p>Explore the topics and insights covered during the first day of the conference.</p>
                  </CardBody>
                  <CardFooter>
                    <span style={{ color: "blue" }}>Read More</span>
                  </CardFooter>
                </div>
              </div>
            </Card>
          </div>

          {/* Day 2 Card with Connected Nodes Pattern */}
          <div
            className={classes.clickableCard}
            onClick={() => handleCardClick('/tedai2024/tedai2024-day2-panels-summary')}
          >
            <Card className={classes.day2Inner}>
              <div style={{ display: "flex", padding: "10px" }}>
                <div style={{
                  width: "150px",
                  flexShrink: 0,
                  marginRight: "10px"
                }}>
                  <img
                    src="/img/tedai2024/gallery2/tedai2024_panels_header2.jpeg"
                    alt="Day 2"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "4px"
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <CardBody>
                    <h4 className={classes.cardTitle}>Day 2: Panels</h4>
                    <p>Insights from panel discussions held on the second day, focusing on future trends in AI.</p>
                  </CardBody>
                  <CardFooter>
                    <span style={{ color: "blue" }}>Read More</span>
                  </CardFooter>
                </div>
              </div>
            </Card>
          </div>
        </div>


      </div>

      <div className={classes.heroSection}>
        <h2 className={classes.sectionTitle}>Key TEDAI2024 Takeouts</h2>


        <ol className={classes.customList}>
          <li>
            <FontAwesomeIcon icon={faBrain} />
            <div>
              <strong>AI has saved millions of hours of research time already(!)</strong> – by running tests and models "in silico" this has led to (and will continue to) ever-accelerating scientific breakthroughs.
            </div>
          </li>
          <li>
            <FontAwesomeIcon icon={faShieldAlt} />
            <div>
              <strong>Military Concerns</strong>
              <ul>
                <li>Can we control the spread of military AI technology like we did for nuclear weapons? (i.e., by controlling supply chains for chips and hardware)</li>
              </ul>
            </div>
          </li>
          <li>
            <FontAwesomeIcon icon={faBolt} />
            <div>
              <strong>Is <u>less</u> AI Regulation in fact better?</strong>
              <ul>
                <li>Regulation favors big tech/incumbents (they already developed their models/tech without regulation) at the expense of smaller players.</li>
              </ul>
            </div>
          </li>
          <li>
            <FontAwesomeIcon icon={faIndustry} />
            <div>
              <strong>Energy demands/cost of today's AI models</strong>
              <ul>
                <li>GPT-4 cost $100 million and thousands of CPUs to train; how much will GPT-5 cost? Should we build nuclear reactors to power AI development?</li>
              </ul>
            </div>
          </li>
          <li>
            <FontAwesomeIcon icon={faRobot} />
            <div>
              <strong>The future of the workforce</strong>
              <ul>
                <li>Should we focus on cutting costs/jobs or work on enhancing current jobs/productivity?</li>
                <li>This technological revolution is similar to past revolutions - jobs will become obsolete, other opportunities and industries will arise.</li>
              </ul>
            </div>
          </li>
        </ol>
      </div>

{/* Carousel Section with Photos */}
<div className={classes.container}>
        <h2 className={classes.sectionTitle}>Top Photos</h2>
        <CarouselVar
          images={[
            {
              src: "/img/tedai2024/gallery2/IMG_3.jpeg",
              alt: "TEDAI 2024 Herbst Theatre",
              location: "Getting ready for TEDAI2024 to start at Herbst Theatre, San Francisco",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_7.jpeg",
              alt: "TEDAI 2024 - Jakob Uszkoreit",
              location: "Jakob Uszkoreit: 'The field is sometimes called alchemy driven by wizards. AI has so far evaded our attempts at explanation.' ",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_9.jpeg",
              alt: "TEDAI 2024 - Surya Ganguly",
              location: "Surya Ganguly: 'The possibilities of bi-directional communication between brains and machines are limitless to understand, cure and enhance the brain'",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_10.jpeg",
              alt: "TEDAI 2024 - Max Jaderberg",
              location: "Max Jaderberg: 'AlphaFold has saved a Billion years of research time'",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_11.jpeg",
              alt: "TEDAI 2024 - Guillaume Verdon",
              location: "Guillaume Verdon: 'Scaling thermodynamic computing could lead up to 100Mx more energy efficiency for AI.'",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_15.jpeg",
              alt: "TEDAI 2024 - Eugenia Kuyda",
              location: "Eugenia Kuyda: 'Will AI companions destroy us or bring us back together?'",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_26.jpeg",
              alt: "TEDAI 2024 - Panels",
              location: "Romack, Rao, Lamm & Mollick: 'How is AI already Transforming the Workforce?'",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_27.jpeg",
              alt: "TEDAI 2024 - Panels",
              location: "Rovelli, Johnson, Ganguly & Jaderberg: 'What is the promise of new discoveries for scientists and creators?'",
              description: "An amazing sunset.",
            },
            {
              src: "/img/tedai2024/gallery2/IMG_35.jpeg",
              alt: "TEDAI 2024 - Panels",
              location: "Uszkoreit, Dao & Domingos: Where is ChatGPT-5, has LLM Hit its Ceiling?",
              description: "An amazing sunset.",
            },
            // Add more images as needed
          ]}
        />

      </div>


        <div className={classes.container}>
          <h2 className={classes.sectionTitle}>Some of the best from TEDAI2024:</h2>
          {isMounted && (
            <div className={classes.cardContainer2}>
             <CardExampleCardRotating
            frontTitle="Carlo Rovelli"
            frontDescription="Theoretical Physicist, World’s 50 Top Thinkers"
            backTitle="Key Topics"
            backDescription={`• Time is a human construct
        • Time may not exist in nature
        • AI's time perception differs`}
            frontImage="/img/tedai2024/headshots/carlos-rovelli.png"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />
          <CardExampleCardRotating
            frontTitle="Jessica Coon"
            frontDescription="Professor of Linguistics, McGill University Linguistics Consultant for the 2016 film 'Arrival'"
            backTitle="Key Topics"
            backDescription={`• Languages have deep structural similarities.
        • Humans reject fake languages
        • LLMs have limited language diversity.`}
            frontImage="/img/tedai2024/headshots/jessica-coon.png"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />
          <CardExampleCardRotating
            frontTitle="Jakob Uszkoreit"
            frontDescription="CEO and Co-Founder, Inceptive Nucleics, Co-Author of seminal paper 'Attention is all you need'"
            backTitle="Key Topics"
            backDescription={`• Human minds are Limited, AI helps
     • Science should embrace noisy data
     • AI should emphasise solutions over theory`}
            frontImage="/img/tedai2024/headshots/jakob-uszkoreit1.png"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />
          <CardExampleCardRotating
            frontTitle="Surya Ganguly"
            frontDescription="Professor of Applied Physics, Associate Director of HAI Institute at Stanford and Venture Partner, Andreessen Horowitz (a16z)"
            backTitle="Key Topics"
            backDescription={`• Humans are efficient learning machines
      • AI should try match biology's efficiency
      • Human brains have predictive energy allocation.`}
            frontImage="/img/tedai2024/headshots/surya-ganguly.png"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />
          <CardExampleCardRotating
            frontTitle="Max Jaderberg"
            frontDescription="Chief AI Officer, Isomorphic Labs. Co-Developer of AlphaStar, Grandmaster Starcraft II agent"
            backTitle="Key Topics"
            backDescription={`• AlphaFold is revolutionising drug design
      • Saved 1 billion years of research
      • Personalized medicine is coming, soon`}
            frontImage="/img/tedai2024/headshots/max-jaderberg1.png"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />
          <CardExampleCardRotating
            frontTitle="Guillaume Verdon"
            frontDescription="Founder & CEO, Extropic, Creator of TensorFlow Quantum"
            backTitle="Key Topics"
            backDescription={`• AI should mimic brain's efficiency
      • Mesoscale is the key to efficient AI
      • Thermo chips are the new AI hardware.`}
            frontImage="/img/tedai2024/headshots/guillaume-verdon.png"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />

          <CardExampleCardRotating
            frontTitle="Ethan Mollick"
            frontDescription="Co-Director, Generative AI Lab, Wharton School of the University of Pennsylvania"
            backTitle="Key Topics"
            backDescription={`• Don't just use AI to cut costs
      • Agentic AI workflows are the future.
      • AI is a strong reasoner, not just summarizer.`}
            frontImage="/img/tedai2024/headshots/ethan-mollick.jpeg"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />

          <CardExampleCardRotating
            frontTitle="Pedro Domingos"
            frontDescription="Professor Emeritus of Computer Science and Engineering, University of Washington"
            backTitle="Key Topics"
            backDescription={`• AI digital twins will run America by 2040
      • Backpropagation is holding AI back.
      • Discrete computation is key to AI.`}
            frontImage="/img/tedai2024/headshots/pedro-domingos.jpeg"
            backImage="/img/tedai2024/gallery2/IMG_3.jpeg"
          />
              
            </div>
          )}

          <h2 className={classes.sectionTitle}>All Photos</h2>
          <p className={classes.introTextCenter}>Click on image below to go to the gallery!</p>
          <a href="/tedai2024/tedai2024-photo-gallery">
            <picture>
              <source
                media="(max-width: 600px)"
                srcSet="/img/tedai2024/home/all-tedai2024-photos-grid-mob.png"
              />
              <source
                media="(min-width: 601px)"
                srcSet="/img/tedai2024/home/all-tedai2024-photos-grid.png"
              />
              <img
                src="/img/tedai2024/home/all-tedai2024-photos-grid.png"
                alt="Photo Gallery"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "4px"
                }}
              />
            </picture>
          </a>
        </div>

        <div className={classes.container}>
        <h2 className={classes.sectionTitle}>Checkout the videos in our fun section!</h2>
        <p className={classes.introTextCenter}> Click on the image below to go to the video gallery</p>
        <a href="/tedai2024/tedai2024-fun-section">
          <picture>
            <source
              media="(max-width: 600px)"
              srcSet="/img/tedai2024/home/fun-section-image-mob.png"
            />
            <source
              media="(min-width: 601px)"
              srcSet="/img/tedai2024/home/fun-section-image.png"
            />
            <img
              src="/img/tedai2024/home/fun-section-image.png"
              alt="Day 2"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "4px"
              }}
            />
          </picture>
        </a>
      </div>

        <Section8Contacts />
        <Footer />
      </div>
      </ClientOnly>
    </>
  );
}
